import React from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Vejica, Val, Srce, ArrowRightThin } from '../components/vd/LineArt';
import { BlobDecor } from '../components/vd/Decor';
import { useReveal } from '../components/vd/useReveal';
import { IconImg, SubIcons } from '../components/vd/IconImg';

/* ---------------- Building blocks ---------------- */

const IntroBox = ({ question, subtext, children, testid }) => (
  <section className="pb-6 sm:pb-8" data-testid={testid || 'subpage-intro-box'}>
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative rounded-[2rem] px-6 py-8 sm:px-14 sm:py-10 text-center bg-vd-beige-soft/55">
        <p className="font-serif italic text-2xl sm:text-3xl text-vd-text leading-[1.45] max-w-xl mx-auto">
          {question || children}
        </p>
        {subtext && (
          <p className="mt-4 text-sm sm:text-base text-vd-muted leading-relaxed max-w-lg mx-auto">
            {subtext}
          </p>
        )}
      </div>
    </div>
  </section>
);

const EXPERT_PHOTOS = {
  'Anja Kikl': 'https://customer-assets-lqy194kg.emergentagent.net/job_31a31169-bb18-4cab-ba3c-d2cff1daa07a/artifacts/cr1pk6qv_Anja%20Kikl2.png',
  'Špela Peternel': 'https://customer-assets-7cd3h4nn.emergentagent.net/job_c982e3b8-8704-4f58-b6fb-f577f967baf4/artifacts/wv00xn4a_Spela%20Peternel.jpg',
  'Amanda Golob': 'https://customer-assets-7cd3h4nn.emergentagent.net/job_c982e3b8-8704-4f58-b6fb-f577f967baf4/artifacts/g91waklf_Amanda%20Golob.jfif',
  'Jasmina Krajnc': 'https://customer-assets-7cd3h4nn.emergentagent.net/job_c982e3b8-8704-4f58-b6fb-f577f967baf4/artifacts/99i4blxk_Jasmina%20Krajnc.jpg',
  'Asja Samec': 'https://customer-assets-7cd3h4nn.emergentagent.net/job_c982e3b8-8704-4f58-b6fb-f577f967baf4/artifacts/69erzi6p_Asja%20Samec.webp',
  'Špela Ferbežar Ban': 'https://customer-assets-7cd3h4nn.emergentagent.net/job_c982e3b8-8704-4f58-b6fb-f577f967baf4/artifacts/8utn8ym9_%C5%A1pela%20ferbe%C5%BEar%20ban.jfif',
};

const PHOTO_STYLES = {
  'Anja Kikl': { transform: 'scale(1.08)', objectPosition: 'center 0%' },
  'Asja Samec': { transform: 'scale(1.55)', objectPosition: 'center 22%' },
};

const ExpertCard = ({ name, description, bullets = [], website, programs = [] }) => {
  const testId = `expert-${name.toLowerCase().replace(/\s+/g, '-')}`;
  const helpHeader = name === 'Špela Ferbežar Ban' ? 'Kako ti lahko pomagam?' : 'Kako ti lahko pomaga?';
  const photo = EXPERT_PHOTOS[name];
  const photoStyle = PHOTO_STYLES[name] || {};
  const initials = name.split(' ').map(s => s[0]).join('').slice(0, 2);
  const websiteHref = website && (website.startsWith('http') ? website : `https://${website}`);

  return (
    <article className="bg-white rounded-2xl border border-vd-soft p-6 sm:p-8 hover-warm-lift" data-testid={testId}>
      <div className="grid md:grid-cols-3 gap-6 items-start">
        <div className="md:col-span-1">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase text-vd-secondary mb-2">Strokovnjakinja</p>
          <h2 className="font-serif italic text-2xl sm:text-3xl text-vd-text mb-3">{name}</h2>
          {photo ? (
            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden border border-vd-soft shadow-sm" data-testid={`avatar-${name.toLowerCase().replace(/\s+/g, '-')}`}>
              <img src={photo} alt={name} className="w-full h-full object-cover" style={photoStyle} loading="lazy" />
            </div>
          ) : (
            <div className="w-24 h-24 rounded-full bg-vd-primary-soft border border-vd-primary/40 flex items-center justify-center text-vd-primary font-serif text-2xl" aria-hidden="true">
              {initials}
            </div>
          )}
        </div>

        <div className="md:col-span-2 space-y-4">
          {description && <p className="text-vd-text/90 leading-relaxed">{description}</p>}

          {bullets.length > 0 && (
            <div>
              <h3 className="font-serif text-lg text-vd-text mb-2">{helpHeader}</h3>
              <ul className="space-y-1.5">
                {bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-vd-text/85 leading-relaxed">
                    <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 bg-vd-accent rounded-full" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {programs.length > 0 && (
            <div className="space-y-4 pt-2">
              <h3 className="font-serif text-lg text-vd-text">{helpHeader}</h3>
              {programs.map((p, i) => (
                <div key={i} className="bg-vd-bg rounded-xl border border-vd-soft p-4 sm:p-5">
                  <h4 className="font-serif text-lg text-vd-text mb-1.5">{p.title}</h4>
                  <p className="text-sm text-vd-text/85 leading-relaxed mb-3">{p.desc}</p>
                  <div className="flex flex-wrap items-center gap-3">
                    <Link
                      to={p.to}
                      className="inline-flex items-center gap-2 bg-vd-accent text-white px-4 py-2 rounded-full text-sm hover:opacity-90 transition-all"
                    >
                      {p.ctaLabel || 'Spoznaj program'} <ArrowRightThin size={14} />
                    </Link>
                    {p.bookingHref && (
                      <a
                        href={p.bookingHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-beige-700 hover:bg-beige-800 text-white px-4 py-2 rounded-full text-sm transition-all"
                        data-testid={`program-book-${i}`}
                      >
                        {p.bookingLabel || 'Rezerviraj srečanje'} <ArrowRightThin size={14} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {website && (
            <p className="text-sm text-vd-muted pt-2">
              Obišči njeno spletno stran:{' '}
              <a
                href={websiteHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-vd-accent hover:underline break-all"
              >
                {website.replace(/^https?:\/\//, '')}
              </a>
            </p>
          )}
        </div>
      </div>
    </article>
  );
};

const BottomLine = () => (
  <section className="py-12" data-testid="subpage-bottom-line">
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <Val size={32} className="text-vd-primary mx-auto mb-3" />
      <p className="text-sm sm:text-base text-vd-muted leading-relaxed">
        Vsaka pot je drugačna. Če danes potrebuješ nekaj drugega, razišči tudi ostala poglavja Val Dotika.
      </p>
    </div>
  </section>
);

const SUB_DECOR_MAP = {
  '/icons/sub_2_prvi_dnevi.png': '/icons/vd_sub_masaza.png',
  '/icons/sub_3_hranjenje.png': '/icons/vd_sub_hranjenje.png',
  '/icons/sub_4_tvoje_telo.png': '/icons/vd_sub_tvoje_telo.png',
  '/icons/sub_5_povezanost.png': '/icons/vd_sub_povezanost.png',
};

const SubpageShell = ({ title, heroIconSrc, HeroIcon, introQuote, experts, testid, meta }) => {
  const heroRef = useReveal();
  const introRef = useReveal();
  const expertsRef = useReveal();

  const isPrviDnevi = testid === 'page-prvi-dnevi';
  const isHranjenje = testid === 'page-uciva-se-hranjenja';
  const isTvojeTelo = testid === 'page-tvoje-telo';
  const isPovezanost = testid === 'page-povezanost';

  return (
    <main className="pb-4 relative overflow-hidden" data-testid={testid}>
      <Helmet>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
      </Helmet>

      {/* Decorative blobs */}
      <BlobDecor className="-top-24 -right-24 opacity-70" size={360} color="rgba(233, 224, 205, 0.55)" />
      <BlobDecor className="top-[70vh] -left-32 opacity-60" size={340} color="rgba(213, 219, 205, 0.45)" variant="slower" />

     <section
  ref={heroRef}
  className={`relative reveal overflow-hidden ${
    isPrviDnevi || isHranjenje || isTvojeTelo || isPovezanost
      ? 'min-h-[470px] lg:min-h-[520px] flex items-center pt-10 sm:pt-14 pb-24 lg:pb-28'
      : 'pt-16 sm:pt-20 pb-8 sm:pb-12'
  }`}
>

  {isPrviDnevi ? (
    <>
      {/* PRVI DNEVI PO PORODU */}

      <div
        aria-hidden="true"
        className="hidden md:block absolute left-[-5%] top-[5%] w-[34rem] lg:w-[42rem] h-[34rem] lg:h-[42rem] rounded-full pointer-events-none z-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(223,210,188,0.28) 0%, rgba(223,210,188,0.14) 48%, rgba(223,210,188,0.05) 68%, transparent 82%)',
          filter: 'blur(18px)',
        }}
      />

      <img
        src="/icons/vd_hero_mama_transparent.png"
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute left-[-3%] lg:left-[1%] bottom-[32px] lg:bottom-[38px] w-[31rem] lg:w-[38rem] opacity-[0.42] pointer-events-none select-none z-0"
        data-testid="subpage-prvi-dnevi-mama"
      />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
  <div className="md:ml-[38%] lg:ml-[40%] text-center">

    <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl text-vd-accent leading-tight">
      {title}
    </h1>

    <div className="mt-8">
      <p className="font-serif italic text-2xl sm:text-3xl text-vd-text leading-[1.4]">
        {introQuote?.question}
      </p>

      {introQuote?.subtext && (
        <p className="mt-4 text-sm sm:text-base text-vd-muted leading-relaxed">
          {introQuote.subtext}
        </p>
      )}
    </div>

  </div>
</div>

      <svg
        aria-hidden="true"
        viewBox="0 0 1440 130"
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 w-full h-[105px] lg:h-[125px] pointer-events-none z-[1]"
      >
        <path
          d="M-40 48 C150 6 290 104 485 55 C680 7 820 111 1015 61 C1200 15 1335 93 1480 48"
          fill="none"
          stroke="rgba(133,119,94,0.25)"
          strokeWidth="1.15"
        />
        <path
          d="M-40 72 C150 32 310 127 500 79 C690 31 840 126 1025 82 C1205 39 1340 114 1480 76"
          fill="none"
          stroke="rgba(133,119,94,0.17)"
          strokeWidth="1"
        />
        <path
          d="M-40 94 C155 58 315 142 510 101 C700 60 850 143 1040 104 C1220 67 1350 132 1480 99"
          fill="none"
          stroke="rgba(133,119,94,0.11)"
          strokeWidth="0.9"
        />
      </svg>
    </>

  ) : isHranjenje ? (

    <>
      {/* UČIVA SE HRANJENJA */}

      <div
        aria-hidden="true"
        className="hidden md:block absolute left-[-4%] top-[4%] w-[35rem] lg:w-[43rem] h-[35rem] lg:h-[43rem] rounded-full pointer-events-none z-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(223,210,188,0.26) 0%, rgba(223,210,188,0.13) 48%, rgba(223,210,188,0.04) 68%, transparent 82%)',
          filter: 'blur(18px)',
        }}
      />

      <img
        src="/icons/vd_hero_hranjenje_mama.png"
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute left-[-1%] lg:left-[3%] bottom-[30px] lg:bottom-[36px] w-[29rem] lg:w-[36rem] opacity-[0.44] pointer-events-none select-none z-0"
        data-testid="subpage-hranjenje-mama"
      />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
  <div className="md:ml-[39%] lg:ml-[41%] text-center">

    <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl text-vd-accent leading-tight">
      {title}
    </h1>

    <div className="mt-8">
      <p className="font-serif italic text-2xl sm:text-3xl text-vd-text leading-[1.4]">
        {introQuote?.question}
      </p>

      {introQuote?.subtext && (
        <p className="mt-4 text-sm sm:text-base text-vd-muted leading-relaxed">
          {introQuote.subtext}
        </p>
      )}
    </div>

  </div>
</div>

      <svg
        aria-hidden="true"
        viewBox="0 0 1440 130"
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 w-full h-[105px] lg:h-[125px] pointer-events-none z-[1]"
      >
        <path
          d="M-40 48 C150 6 290 104 485 55 C680 7 820 111 1015 61 C1200 15 1335 93 1480 48"
          fill="none"
          stroke="rgba(133,119,94,0.25)"
          strokeWidth="1.15"
        />
        <path
          d="M-40 72 C150 32 310 127 500 79 C690 31 840 126 1025 82 C1205 39 1340 114 1480 76"
          fill="none"
          stroke="rgba(133,119,94,0.17)"
          strokeWidth="1"
        />
        <path
          d="M-40 94 C155 58 315 142 510 101 C700 60 850 143 1040 104 C1220 67 1350 132 1480 99"
          fill="none"
          stroke="rgba(133,119,94,0.11)"
          strokeWidth="0.9"
        />
      </svg>
        </>

  ) : isTvojeTelo ? (

    <>
      {/* TVOJE TELO */}

      {/* Nežen bež oblak za ilustracijo */}
      <div
        aria-hidden="true"
        className="hidden md:block absolute left-[-4%] top-[4%] w-[35rem] lg:w-[43rem] h-[35rem] lg:h-[43rem] rounded-full pointer-events-none z-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(223,210,188,0.24) 0%, rgba(223,210,188,0.12) 48%, rgba(223,210,188,0.04) 68%, transparent 82%)',
          filter: 'blur(18px)',
        }}
      />

      {/* Nova ilustracija Tvoje telo */}
      <img
        src="/icons/vd_hero_tvoje_telo.png"
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute left-[-1%] lg:left-[2%] bottom-[26px] lg:bottom-[34px] w-[30rem] lg:w-[37rem] opacity-[0.38] pointer-events-none select-none z-0"
        style={{
          filter: 'grayscale(0.65) sepia(0.22) saturate(0.42)',
        }}
        data-testid="subpage-tvoje-telo-mama"
      />

      {/* Naslov + citat */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="md:ml-[39%] lg:ml-[41%] text-center">

          <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl text-vd-accent leading-tight">
            {title}
          </h1>

          <div className="mt-8">
            <p className="font-serif italic text-2xl sm:text-3xl text-vd-text leading-[1.4]">
              {introQuote?.question}
            </p>

            {introQuote?.subtext && (
              <p className="mt-4 text-sm sm:text-base text-vd-muted leading-relaxed">
                {introQuote.subtext}
              </p>
            )}
          </div>

        </div>
      </div>

      {/* Valovi spodaj */}
      <svg
        aria-hidden="true"
        viewBox="0 0 1440 130"
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 w-full h-[105px] lg:h-[125px] pointer-events-none z-[1]"
      >
        <path
          d="M-40 48 C150 6 290 104 485 55 C680 7 820 111 1015 61 C1200 15 1335 93 1480 48"
          fill="none"
          stroke="rgba(133,119,94,0.25)"
          strokeWidth="1.15"
        />
        <path
          d="M-40 72 C150 32 310 127 500 79 C690 31 840 126 1025 82 C1205 39 1340 114 1480 76"
          fill="none"
          stroke="rgba(133,119,94,0.17)"
          strokeWidth="1"
        />
        <path
          d="M-40 94 C155 58 315 142 510 101 C700 60 850 143 1040 104 C1220 67 1350 132 1480 99"
          fill="none"
          stroke="rgba(133,119,94,0.11)"
          strokeWidth="0.9"
        />
      </svg>
        </>

  ) : isPovezanost ? (

    <>
      {/* POVEZANOST */}

      <div
        aria-hidden="true"
        className="hidden md:block absolute left-[-4%] top-[4%] w-[35rem] lg:w-[43rem] h-[35rem] lg:h-[43rem] rounded-full pointer-events-none z-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(223,210,188,0.24) 0%, rgba(223,210,188,0.12) 48%, rgba(223,210,188,0.04) 68%, transparent 82%)',
          filter: 'blur(18px)',
        }}
      />

      <img
        src="/icons/vd_hero_povezanost_mama.png"
        alt=""
        aria-hidden="true"
        className="hidden md:block absolute left-[1%] lg:left-[4%] bottom-[28px] lg:bottom-[34px] w-[28rem] lg:w-[35rem] opacity-[0.40] pointer-events-none select-none z-0"
        style={{
          filter: 'grayscale(0.35) sepia(0.18) saturate(0.60)',
        }}
        data-testid="subpage-povezanost-mama"
      />

      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 sm:px-10 lg:px-16">
        <div className="md:ml-[39%] lg:ml-[41%] text-center">

          <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl text-vd-accent leading-tight">
            {title}
          </h1>

          <div className="mt-8">
            <p className="font-serif italic text-2xl sm:text-3xl text-vd-text leading-[1.4]">
              {introQuote?.question}
            </p>

            {introQuote?.subtext && (
              <p className="mt-4 text-sm sm:text-base text-vd-muted leading-relaxed">
                {introQuote.subtext}
              </p>
            )}
          </div>

        </div>
      </div>

      <svg
        aria-hidden="true"
        viewBox="0 0 1440 130"
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 w-full h-[105px] lg:h-[125px] pointer-events-none z-[1]"
      >
        <path
          d="M-40 48 C150 6 290 104 485 55 C680 7 820 111 1015 61 C1200 15 1335 93 1480 48"
          fill="none"
          stroke="rgba(133,119,94,0.25)"
          strokeWidth="1.15"
        />
        <path
          d="M-40 72 C150 32 310 127 500 79 C690 31 840 126 1025 82 C1205 39 1340 114 1480 76"
          fill="none"
          stroke="rgba(133,119,94,0.17)"
          strokeWidth="1"
        />
        <path
          d="M-40 94 C155 58 315 142 510 101 C700 60 850 143 1040 104 C1220 67 1350 132 1480 99"
          fill="none"
          stroke="rgba(133,119,94,0.11)"
          strokeWidth="0.9"
        />
      </svg>
    </>

  ) : (

    <>
      {/* OSTALE PODSTRANI OSTANEJO KOT PREJ */}

      {heroIconSrc && (
        <img
          src={SUB_DECOR_MAP[heroIconSrc] || heroIconSrc}
          alt=""
          aria-hidden="true"
          className="hidden md:block absolute left-2 lg:left-8 top-2 lg:top-4 w-64 lg:w-[22rem] opacity-[0.3] pointer-events-none select-none"
          data-testid="subpage-hero-decor"
        />
      )}

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {heroIconSrc ? (
          <IconImg
            src={heroIconSrc}
            size={148}
            className="mx-auto mb-6 animate-breathe md:hidden"
            alt=""
          />
        ) : HeroIcon ? (
          <HeroIcon
            size={132}
            className="text-vd-primary mx-auto mb-6 vd-line-anim animate-breathe"
          />
        ) : null}

        <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl text-vd-accent leading-tight">
          {title}
        </h1>
      </div>
    </>
  )}

</section>

{!(isPrviDnevi || isHranjenje || isTvojeTelo || isPovezanost) && (
  <div ref={introRef} className="reveal">
    <IntroBox
      testid="subpage-intro"
      {...(
        introQuote &&
        typeof introQuote === 'object' &&
        !introQuote.type
          ? introQuote
          : { children: introQuote }
      )}
    />
  </div>
)}

      <section className="pt-4 pb-4 relative">
        <div ref={expertsRef} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6 reveal-stagger">
          {experts}
        </div>
      </section>

      <BottomLine />
    </main>
  );
};

/* ---------------- Pages ---------------- */

export const PrviDneviPage = () => (
  <SubpageShell
    testid="page-prvi-dnevi"
    heroIconSrc={SubIcons.PrviDnevi}
    title="Prvi dnevi po porodu"
    introQuote={{
      question: <>»Ali to delam prav?«</>,
      subtext: 'Veš kaj, tudi jaz sem se to vprašala. In ne samo enkrat.',
    }}
    meta={{ title: 'Prvi dnevi po porodu – Val Dotika', description: 'Interdisciplinarna pomoč po porodu: Špela Peternel, Amanda Golob, Jasmina Krajnc, Špela Ferbežar Ban in Asja Samec.' }}
    experts={<>
      <ExpertCard
        name="Špela Peternel"
        description="Ženske spremlja skozi nosečnost, porod in prve korake materinstva. Ustvarja varen prostor, kjer lahko mamica zaupa sebi, svojim občutkom in svoji intuiciji."
        bullets={['Priprava na porod', 'Obporodna podpora', 'Priprava na prve tedne materinstva']}
        website="dulaspela.si"
      />
      <ExpertCard
        name="Amanda Golob"
        description="Mamice podpira pri dojenju, črpanju in hranjenju dojenčka brez obsojanja in nepotrebnega pritiska. Verjame, da hranjenje ni le način, kako otrok dobi mleko, ampak tudi čas za bližino, zaupanje in povezanost. Verjame tudi, da za dobrobit dojenčka najprej potrebujemo mamo, ki se počuti slišana, podprta in varna. Zato pri svojem delu združuje strokovno znanje o hranjenju dojenčkov s celostno podporo mami, tudi s pomočjo hipnoterapije."
        bullets={['Svetovanje za dojenje', 'Črpanje mleka', 'Hranjenje po steklenički', 'Izzivi v prvih tednih hranjenja', 'Hipnoterapija za mamice']}
        website="feedbond.si"
      />
      <ExpertCard
        name="Jasmina Krajnc"
        description="Ženskam pomaga ponovno začutiti svoje telo po porodu ter razumeti pomen medeničnega dna in telesnega jedra. Pri svojem delu združuje strokovno znanje, nežnost in spoštovanje do ženskega telesa."
        bullets={['Pregled po porodu', 'Medenično dno', 'Funkcionalno jedro', 'Okrevanje po porodu']}
        website="www.perineum.si"
      />
      <ExpertCard
        name="Asja Samec"
        description="Nosečnicam in mamicam pomaga najti več notranjega miru ter zaupanja vase v obdobjih velikih življenjskih sprememb. Verjame, da vsaka mama potrebuje prostor, kjer je lahko slišana, sprejeta in točno takšna, kot je."
        bullets={['Psihološka podpora po porodu', 'Predelovanje čustvenih stisk', 'Praktična podpora na domu', 'Podporna skupina']}
        website="www.novorojena.si"
      />
      <ExpertCard
        name="Špela Ferbežar Ban"
        description="Nobena mamica ne pride iz istega razloga. Ena zaradi krčkov, druga zato, ker si želi bolje spoznati svojega dojenčka. Tretja pa preprosto zato, ker čuti, da bi ji dobro delo malo podpore."
        programs={[
          { title: 'Pomoč pri trebuščku', desc: 'Nežna masaža kot podpora pri krčkih, napenjanju in prvih težavah s trebuščkom.', to: '/programi/trebuscek' },
        ]}
      />
    </>}
  />
);

export const UcivaSeHranjenjaPage = () => (
  <SubpageShell
    testid="page-uciva-se-hranjenja"
    heroIconSrc={SubIcons.Hranjenje}
    title="Učiva se hranjenja"
    introQuote={{
      question: <>»Kako naj sploh vem, da dobi dovolj mleka?«</>,
      subtext: 'Če se to sprašuješ tudi ti, verjemi, nisi edina.',
    }}
    meta={{ title: 'Učiva se hranjenja – Val Dotika', description: 'Podpora pri dojenju, črpanju in hranjenju dojenčka z Amando Golob.' }}
    experts={<>
      <ExpertCard
        name="Amanda Golob"
        description="Mamice podpira pri dojenju, črpanju in hranjenju dojenčka brez obsojanja in nepotrebnega pritiska. Verjame, da hranjenje ni le način, kako otrok dobi mleko, ampak tudi čas za bližino, zaupanje in povezanost. Verjame tudi, da za dobrobit dojenčka najprej potrebujemo mamo, ki se počuti slišana, podprta in varna. Zato pri svojem delu združuje strokovno znanje o hranjenju dojenčkov s celostno podporo mami, tudi s pomočjo hipnoterapije."
        bullets={['Svetovanje za dojenje', 'Črpanje mleka', 'Hranjenje po steklenički', 'Izzivi v prvih tednih hranjenja', 'Hipnoterapija za mamice']}
        website="feedbond.si"
      />
    </>}
  />
);

export const TvojeTeloPage = () => (
  <SubpageShell
    testid="page-tvoje-telo"
    heroIconSrc={SubIcons.TvojeTelo}
    title="Tvoje telo"
    introQuote={{
      question: <>»Kdaj bom spet jaz?«</>,
      subtext: 'Mogoče si na to pomislila že prve dni. Mogoče šele takrat, ko se je začetni vrvež malo umiril.',
    }}
    meta={{ title: 'Tvoje telo – Val Dotika', description: 'Podpora ženskemu telesu po porodu: Anja Kikl in Jasmina Krajnc.' }}
    experts={<>
      <ExpertCard
        name="Anja Kikl"
        description="Ženskam pomaga bolje razumeti svoje telo, menstrualni cikel in odkriti, kaj jih ovira na poti do želenega cilja. Verjame, da se z majhnimi, dolgoročnimi spremembami začnejo največje poti do boljšega počutja in priprave na nosečnost."
        bullets={['Odnos do telesa in prehrane', 'Menstrualni cikel', 'PCOS', 'Priprava na nosečnost', 'Žensko zdravje']}
        website="anjakikl.si"
      />
      <ExpertCard
        name="Jasmina Krajnc"
        description="Ženskam pomaga ponovno začutiti svoje telo po porodu ter razumeti pomen medeničnega dna in telesnega jedra. Pri svojem delu združuje strokovno znanje, nežnost in spoštovanje do ženskega telesa."
        bullets={['Medenično dno', 'Funkcionalno jedro', 'Varno okrevanje', 'Vračanje zaupanja v telo']}
        website="www.perineum.si"
      />
    </>}
  />
);

export const PovezanostPage = () => (
  <SubpageShell
    testid="page-povezanost"
    heroIconSrc={SubIcons.Povezanost}
    title="Povezanost"
    introQuote={{
      question: <>»Zakaj nimam takšnega občutka, kot sem pričakovala?«</>,
      subtext: 'O tem se govori veliko manj, kot bi si mislila.',
    }}
    meta={{ title: 'Povezanost – Val Dotika', description: 'Notranji mir, pogovor in tečaj masaže dojenčka IAIM.' }}
    experts={<>
      <ExpertCard
        name="Asja Samec"
        description="Nosečnicam in mamicam pomaga najti več notranjega miru ter zaupanja vase v obdobjih velikih življenjskih sprememb. Verjame, da vsaka mama potrebuje prostor, kjer je lahko slišana, sprejeta in točno takšna, kot je."
        bullets={['Psihološka podpora', 'Pogovor brez obsojanja', 'Praktična podpora na domu', 'Podporna skupina']}
        website="www.novorojena.si"
      />
      <ExpertCard
        name="Špela Ferbežar Ban"
        description="Nobena mamica ne pride iz istega razloga. Ena zaradi krčkov, druga zato, ker si želi bolje spoznati svojega dojenčka. Tretja pa preprosto zato, ker čuti, da bi ji dobro delo malo podpore."
        programs={[
          { title: 'Tečaj masaže dojenčka IAIM', desc: 'Nežen dotik, ki krepi povezanost, zaupanje in skupne trenutke med mamico in dojenčkom.', to: '/programi/masaza-dojencka' },
        ]}
      />
    </>}
  />
);

export const OValDotikaPage = () => {
  return (
    <main
  className="pb-12 relative overflow-hidden"
  data-testid="page-o-val-dotika"
>
      <Helmet>
        <title>O Val Dotika – zgodba in vizija</title>
        <meta name="description" content="Zgodba, vizija in vrednote Val Dotika – nastajajočega digitalnega prostora podpore za mamice." />
      </Helmet>
      {/* Botanika skozi celotno stran O Val Dotika */}

{/* LEVA – zgoraj, dvignjena in mehko zaključena */}
<div
  aria-hidden="true"
  className="hidden md:block absolute left-[-10%] top-[-8%] w-[40rem] lg:w-[48rem] pointer-events-none z-0"
  style={{
    WebkitMaskImage:
      'linear-gradient(to bottom, black 0%, black 38%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.55) 62%, rgba(0,0,0,0.25) 73%, rgba(0,0,0,0.08) 82%, transparent 90%)',
    maskImage:
      'linear-gradient(to bottom, black 0%, black 38%, rgba(0,0,0,0.85) 50%, rgba(0,0,0,0.55) 62%, rgba(0,0,0,0.25) 73%, rgba(0,0,0,0.08) 82%, transparent 90%)',
  }}
>
  <img
    src="/icons/vd_botanical_mothers_left.png"
    alt=""
    className="block w-full h-auto opacity-[0.11] select-none"
  />
</div>

{/* DESNA – zgornja sredina */}
<img
  src="/icons/vd_botanical_mothers_right.png"
  alt=""
  aria-hidden="true"
  className="hidden md:block absolute right-[-10%] top-[27%] w-[46rem] lg:w-[56rem] opacity-[0.10] pointer-events-none select-none z-0"
/>

{/* LEVA – spodnja sredina, dvignjena dovolj visoko brez vidnega reza */}
<div
  aria-hidden="true"
  className="hidden md:block absolute left-[-11%] top-[18%] w-[36rem] lg:w-[44rem] pointer-events-none z-0"
  style={{
    WebkitMaskImage:
      'linear-gradient(to bottom, black 0%, black 35%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0.50) 58%, rgba(0,0,0,0.22) 70%, rgba(0,0,0,0.06) 82%, transparent 90%)',
    maskImage:
      'linear-gradient(to bottom, black 0%, black 35%, rgba(0,0,0,0.85) 45%, rgba(0,0,0,0.50) 58%, rgba(0,0,0,0.22) 70%, rgba(0,0,0,0.06) 82%, transparent 90%)',
  }}
>
  <img
    src="/icons/vd_botanical_mothers_left.png"
    alt=""
    className="block w-full h-auto opacity-[0.10] select-none"
  />
</div>

{/* DESNA – spodaj */}
<img
  src="/icons/vd_botanical_mothers_right.png"
  alt=""
  aria-hidden="true"
  className="hidden md:block absolute right-[-10%] top-[77%] w-[46rem] lg:w-[56rem] opacity-[0.10] pointer-events-none select-none z-0"
/>
      
      {/* Elegant editorial decorations */}
<section className="pt-8 sm:pt-12 pb-8 relative z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-serif italic text-4xl sm:text-5xl lg:text-6xl text-vd-accent mb-4">O Val Dotika</h1>
          <p className="text-lg sm:text-xl text-vd-muted leading-relaxed max-w-2xl mx-auto">
            Prostor, kjer ideja o sodobnem poporodnem centru zaživi digitalno.
          </p>
        </div>
      </section>

      <section className="py-10 lg:py-14 relative z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div>
            <h2 className="font-serif italic text-3xl text-vd-text mb-4">Kaj je Val Dotika</h2>
            <div className="space-y-4 text-vd-text/90 leading-relaxed">
              <p>Predstavljaj si, da bi lahko v prvih 40 dneh po porodu na enem mestu našla podporo z različnih področij.</p>
              <p>Prav to želim ustvariti z Valom Dotika.</p>
              <p>Ne prostora, kjer bi ena oseba imela vse odgovore, ampak prostor, kjer se povezujejo strokovnjakinje, ki se med seboj dopolnjujejo in jim tudi sama zaupam.</p>
              <p>Da lahko takrat, ko potrebuješ pomoč, lažje najdeš pravo osebo zase.</p>
            </div>
          </div>

          <div>
            <ul className="grid sm:grid-cols-2 gap-4 text-sm text-vd-text/90">
  {['Amanda Golob', 'Anja Kikl', 'Asja Samec', 'Jasmina Krajnc', 'Špela Ferbežar Ban', 'Špela Peternel'].map((n, i) => (
    <li
      key={i}
      className="vd-expert-name-card relative px-6 py-4 text-center"
    >
      <span className="relative z-10">
        {n}
      </span>
    </li>
  ))}
</ul>
          </div>

        </div>
      </section>
    </main>
  );
};
